import { Layer, TimeIncidence, Space } from '~/packages/general/variable/spaceTime.types'
import { StatsItem } from './statsAndGeoToFeatureCollection.util'
import { StyleForLayer } from './styleForLayer.util'
import chroma from 'chroma-js'

/**
 * Hierarquia geográfica do sistema, do maior para o menor
 */
export const GEOGRAPHICAL_HIERARCHY = [
  'country',
  'macroRegion',
  'state',
  'mesoRegion',
  'microRegion',
  'municipality',
  'neighborhood',
  'street',
] as const

/**
 * Função para verificar se uma categoryKey é específica.
 */
export const isSpecific = (categoryKey: string): boolean =>
  /Segment|Maturity|Size|Division|Courses/.test(categoryKey)

/**
 * Verifica se duas layers têm o mesmo título de variável
 * Inclui regras especiais para títulos relacionados
 */
export const haveSameTitle = (layer0: Layer, layer1: Layer): boolean => {
  const title0 = layer0.variable.title
  const title1 = layer1.variable.title
  return title0 === title1
}

/**
 * Verifica se uma layer é geral (não específica).
 */
export const isGeneralLayer = (layer: Layer): boolean => !isSpecific(String(layer.variable.categoryKey))

/**
 * Verifica se uma layer é específica
 */
export const isSpecificLayer = (layer: Layer): boolean =>
  isSpecific(String(layer.variable.categoryKey))

/**
 * Casos especiais fora do domínio gov ficam desativados.
 */
const specialCasesForEnrollments = (
  _layer0Title: string,
  _layer0CategoryKey: string,
  _layer1Title: string,
  _layer1CategoryKey: string,
) => {
  return false
}

/** Regra especial desativada para categorias que não existem no domínio gov. */
const specialCasesForMonitor = (
  _layer0Title: string,
  _layer0CategoryKey: string,
  _layer1Title: string,
  _layer1CategoryKey: string,
) => false

/**
 * Verifica se duas layers têm potencial para usar coverage choropleth
 * baseado nas regras existentes mais a verificação de contenção geográfica
 *
 * @param layer0 - Primeira layer (deve ser geral e conter a segunda)
 * @param layer1 - Segunda layer (deve ser específica e estar contida na primeira)
 * @param isSpecificFn - Função para verificar se uma categoryKey é específica
 * @returns true se as layers podem usar coverage choropleth
 */
export function canUseCoverageChoropleth(
  layer0: Layer,
  layer1: Layer,
  isSpecificFn?: (categoryKey: string) => boolean,
): boolean {
  // Regras especiais para combinações conhecidas
  const specialCase =
    specialCasesForEnrollments(
      layer0.variable.title,
      String(layer0.variable.categoryKey),
      layer1.variable.title,
      String(layer1.variable.categoryKey),
    ) ||
    specialCasesForMonitor(
      layer0.variable.title,
      String(layer0.variable.categoryKey),
      layer1.variable.title,
      String(layer1.variable.categoryKey),
    )
  if (specialCase) return true

  const checkSpecific = isSpecificFn || isSpecific

  // Verificações básicas
  const sameTitle = haveSameTitle(layer0, layer1)
  const layer0IsGeneral = !checkSpecific(String(layer0.variable.categoryKey))
  const layer1IsSpecific = checkSpecific(String(layer1.variable.categoryKey))

  const result = sameTitle && layer0IsGeneral && layer1IsSpecific

  return result
}

/**
 * Verifica se o período da layer1 está de acordo com o período da layer0
 * @param layer0Time - Período da layer0
 * @param layer1Time - Período da layer1
 * @returns true se o período da layer1 está de acordo com o período da layer0
 */
export function timeContainment(layer0Time: TimeIncidence, layer1Time: TimeIncidence): boolean {
  if (!layer0Time || !layer1Time) return false

  const layer0Start = new Date(layer0Time.startDate!)
  const layer0End = new Date(layer0Time.endDate!)
  const layer1Start = new Date(layer1Time.startDate!)
  const layer1End = new Date(layer1Time.endDate!)

  const layer1Duration = layer1End.getTime() - layer1Start.getTime()
  const layer0Duration = layer0End.getTime() - layer0Start.getTime()

  if (layer1Duration > layer0Duration) return false
  if (layer1Start < layer0Start || layer1End > layer0End) return false

  return true
}

/**
 * Verifica se duas layers estão no mesmo espaço geográfico
 * @param space0 - Espaço da primeira layer
 * @param space1 - Espaço da segunda layer
 * @returns true se as layers estão no mesmo espaço geográfico, false caso contrário
 */
export function areLayersInSameSpace(space0: Space, space1: Space): boolean {
  return (
    space0.delimitationType === space1.delimitationType &&
    //@ts-expect-error
    space0.delimitationId === space1.delimitationId
  )
}

/**
 * Calcula o mapa de cobertura baseado nas estatísticas de duas camadas
 * @param stats0 - Estatísticas da primeira layer
 * @param stats1 - Estatísticas da segunda layer
 * @returns mapa de cobertura, null caso não haja estatísticas
 */
export function calculateCoverageMap(
  stats0: StatsItem[] | null,
  stats1: StatsItem[] | null,
): Record<string | number, number> | null {
  if (!stats0 || !stats1) return null

  const map0 = new globalThis.Map<string | number, number>()
  stats0.forEach(item => {
    if (item.geoId != null) map0.set(item.geoId, Number(item.value) || 0)
  })

  const coverageMap: Record<string | number, number> = {}
  stats1.forEach(item => {
    if (item.geoId == null) return
    const denominator = map0.get(item.geoId) || 0
    const value = Number(item.value) || 0
    coverageMap[item.geoId] = denominator > 0 ? (value / denominator) * 100 : 0
  })

  return coverageMap
}

/**
 * Calcula o range (min/max) dos valores de cobertura
 * @param coverageMap - Mapa de cobertura
 * @returns range (min/max) dos valores de cobertura
 */
export function calculateCoverageRange(coverageMap: Record<string | number, number> | null): {
  min: number
  max: number
} {
  if (!coverageMap) return { min: 0, max: 100 }

  const coverageValues = Object.values(coverageMap).filter(
    value => typeof value === 'number' && !isNaN(value) && isFinite(value),
  )

  if (coverageValues.length === 0) return { min: 0, max: 100 }

  const actualMin = Math.min(...coverageValues)
  const actualMax = Math.max(...coverageValues)

  if (actualMin === actualMax) return { min: 0, max: actualMax || 100 }

  return { min: actualMin, max: actualMax }
}

/**
 * Verifica se as condições para usar coverage choropleth estão atendidas
 * @param layers - Layers
 * @param canUseChoroplethInSecondLayer - Se o coverage choropleth pode ser usado na segunda layer
 * @returns true se as condições para usar coverage choropleth estão atendidas, false caso contrário
 */
export function shouldUseCoverageChoropleth(
  layers: Layer[],
  canUseChoroplethInSecondLayer: { value: boolean; isLoading: boolean },
): boolean {
  return (
    layers.length === 2 &&
    canUseCoverageChoropleth(layers[0], layers[1]) &&
    canUseChoroplethInSecondLayer.value &&
    !canUseChoroplethInSecondLayer.isLoading
  )
}

/**
 * Verifica as condições iniciais para verificação de contenção geográfica
 * @param layers - Layers
 * @returns true se as condições para verificação de contenção geográfica estão atendidas, false caso contrário
 */
export function shouldCheckGeographicalContainment(layers: Layer[]): {
  shouldCheck: boolean
  canUseCoverage: boolean
  isWithinTimeRange: boolean
} {
  if (layers.length !== 2) {
    return { shouldCheck: false, canUseCoverage: false, isWithinTimeRange: false }
  }

  const canUseCoverage = canUseCoverageChoropleth(layers[0], layers[1])
  const isWithinTimeRange = timeContainment(layers[0].time, layers[1].time)

  return {
    shouldCheck: canUseCoverage && isWithinTimeRange,
    canUseCoverage,
    isWithinTimeRange,
  }
}

/**
 * Ajusta os estilos das legendas baseado no estado das camadas
 * @param styles - Estilos das legendas
 * @param canUseCoverage - Se o coverage choropleth pode ser usado
 * @param sameSpace - Se as layers estão no mesmo espaço geográfico
 * @param coverageRange - Range dos valores de cobertura
 * @returns estilos das legendas ajustados
 */
export function adjustLegendStyles(
  styles: StyleForLayer[],
  canUseCoverage: boolean,
  sameSpace: boolean,
  coverageRange: { min: number; max: number },
): StyleForLayer[] {
  if (canUseCoverage) {
    return styles.map((style, index) =>
      index === 1
        ? {
            ...style,
            min: coverageRange.min,
            max: coverageRange.max,
            valueType: 'percentageCustom' as any,
          }
        : { ...style, valueType: 'hidden' as any },
    )
  } else if (sameSpace) {
    // Mesmo espaço mas não elegível - esconder primeira legenda
    return styles.map((style, index) =>
      index === 0 ? { ...style, valueType: 'hidden' as any } : style,
    )
  }

  return styles
}

/**
 * Cria o mapStyle para coverage choropleth
 * @param coverageMap - Mapa de cobertura
 * @param coverageRange - Range dos valores de cobertura
 * @param colorMap - Cor do mapa
 * @returns mapStyle para coverage choropleth
 */
export function createCoverageMapStyle(
  coverageMap: Record<string | number, number> | null,
  coverageRange: { min: number; max: number },
  colorMap: string[],
) {
  return ({ geoId }: { geoId?: string | number }) => {
    const cov = geoId != null && coverageMap ? coverageMap[geoId] : undefined
    const covValue = typeof cov === 'number' ? cov : 0
    const min = coverageRange.min
    const max = coverageRange.max
    const ratio = max === min ? 1 : (covValue - min) / (max - min)

    const colorMapFunction = chroma.scale(colorMap).mode('lrgb')
    const chromaColor = colorMapFunction(ratio)

    return {
      fillColor: chromaColor.hex(),
      strokeColor: chromaColor.darken().hex(),
      strokeWeight: 1,
      strokeOpacity: 1,
      fillOpacity: 0.7,
      zIndex: 2,
    }
  }
}

/**
 * Cria o mapStyle para a primeira camada quando coverage está ativo
 * @returns mapStyle para a primeira camada quando coverage está ativo
 */
export function createFirstLayerMapStyle() {
  return () => ({
    fillColor: '#AEE8FF',
    strokeColor: '#AEE8FF',
    strokeWeight: 1,
    strokeOpacity: 0.1,
    fillOpacity: 0,
    zIndex: 0,
  })
}

/**
 * Verifica se uma camada deve ser renderizada no mapa,
 * para casos quando Cloroplético não pode ser usado
 * e/ou quando as camadas estão no mesmo espaço geográfico
 * @param layerIndex - Índice da camada
 * @param layers - Array de camadas
 * @param canUseChoroplethInSecondLayer - Estado do choropleth na segunda camada
 * @returns true se a camada deve ser renderizada, false caso contrário
 */
export function shouldRenderLayer(
  layerIndex: number,
  layers: Layer[],
  canUseChoroplethInSecondLayer: { value: boolean; isLoading: boolean },
): boolean {
  if (layers.length !== 2) return true

  if (layerIndex === 0) {
    const sameSpace = areLayersInSameSpace(layers[0].space, layers[1].space)

    if (sameSpace) return false

    // Se coverage choropleth está ativo, não renderizar a primeira camada (ela fica com fillOpacity: 0)
    const isCoverageActive =
      canUseChoroplethInSecondLayer.value && !canUseChoroplethInSecondLayer.isLoading
    if (isCoverageActive) return false
  }

  return true
}
